/**
 * Generation step — shared vs unique texts, the AI texts per drop (the
 * same number the Text ranking's Config shows as ✦), and an
 * AI-call footnote that matches the ENGINE's math (SHARED = one call per
 * container per generation; a drop whose texts are proven only costs none),
 * never a prettier fiction.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { HintDot, SegmentedPills } from '@/components/ui';

import { dropCount } from '../general/slots/step-summaries';
import type { ContainerEditor } from '../state/useContainerEditor';

const MICRO = 'flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

export const GenerationStep: FC<{ editor: ContainerEditor }> = ({ editor }) => {
  const { creative } = editor.draft;
  // The engine's count (plan-totals): one call per DROP — a time with two
  // list groups sends two drops (QA 18/10/2026: this counted times).
  const drops = dropCount(editor.draft);
  const unique = creative.textMode === 'UNIQUE_PER_DROP';
  const calls = creative.amount === 0 ? 0 : unique ? drops : 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <span className={MICRO}>
            Texts per drop
            <HintDot label="Shared = one AI generation per container, reused across its drops. Unique per drop = one AI call per drop — 20 drops a day mean 20 calls. Creative is generated only for new drafts." />
          </span>
          <SegmentedPills
            options={[
              { value: 'SHARED', label: 'Shared set' },
              { value: 'UNIQUE_PER_DROP', label: 'Unique per drop' },
            ]}
            value={creative.textMode}
            onChange={(textMode) => editor.creativePatch({ textMode })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={MICRO}>
            AI texts per drop
            <HintDot label="Fresh texts the AI writes on each drop. Proven texts from the ranking can ride on the same drop next to them — Text ranking › Config (0 here = proven texts only there)." />
          </span>
          <input
            type="number"
            min={0}
            max={15}
            value={creative.amount}
            onChange={(e) => {
              const n = Math.floor(Number(e.target.value));
              if (Number.isFinite(n) && n >= 0 && n <= 15) editor.creativePatch({ amount: n });
            }}
            className="h-9 w-20 rounded-md border border-border/60 bg-background px-3 text-sm tabular-nums outline-none focus:border-primary"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {creative.amount === 0 ? (
          <>
            no AI text — <span className="font-mono text-foreground">0 AI text calls</span> per generation (proven texts only, Text ranking › Config)
          </>
        ) : unique ? (
          <>
            unique per drop × {drops} drop
            {drops === 1 ? '' : 's'} a day = <span className="font-mono text-foreground">{calls} AI text call{calls === 1 ? '' : 's'}</span> per generation
          </>
        ) : (
          <>
            shared set = <span className="font-mono text-foreground">{calls} AI text call</span> per generation
          </>
        )}{' '}
        · senders live on the <b>Gateways</b> tab
      </p>
    </div>
  );
};
